import { useEffect, useRef, memo } from "react";

interface BackgroundHeartsProps {
  count?: number;
}

interface Heart {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  mass: number;
  rotation: number;
  rotationSpeed: number;
  squashX: number;
  squashY: number;
}

const BackgroundHearts = memo(({ count = 15 }: BackgroundHeartsProps) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const heartsRef = useRef<Heart[]>([]);
  const heartElementsRef = useRef<HTMLDivElement[]>([]);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    if (!canvasRef.current) return;

    const container = canvasRef.current;

    /* ----------------------------
       Constants
    -----------------------------*/

    const RESTITUTION = 0.95; // Bounciness
    const POSITION_CORRECTION = 0.4;
    const SQUASH_RECOVERY = 0.2;
    const MARGIN = 120;

    const MAINV = 1;
    const OTHERV = 0.5;
    const MINV = 0.1;
    const ROTATIONV = 0.5;

    /* ----------------------------
       Creation
    -----------------------------*/

    const createHeart = (): Heart => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      const size = 100 + Math.random() * 50;

      const edge = Math.floor(Math.random() * 4);

      let x = 0;
      let y = 0;
      let vx = 0;
      let vy = 0;

      switch (edge) {
        case 0: // Top
          x = Math.random() * width;
          y = -size;
          vx = (Math.random() - 0.5) * MAINV; // -1.5 - 1.5
          vy = Math.random() * OTHERV + MINV;   // 0.5 - 2.5
          break;

        case 1: // Right
          x = width + size;
          y = Math.random() * height;
          vx = -(Math.random() * OTHERV + MINV); // 0.5 - 2.5
          vy = (Math.random() - 0.5) * MAINV;  // -1.5 - 1.5
          break;

        case 2: // Bottom
          x = Math.random() * width;
          y = height + size;
          vx = (Math.random() - 0.5) * MAINV;
          vy = -(Math.random() * OTHERV + MINV);
          break;

        default: // Left
          x = -size;
          y = Math.random() * height;
          vx = Math.random() * OTHERV + MINV;
          vy = (Math.random() - 0.5) * MAINV;
      }

      return {
        x,
        y,
        vx,
        vy,
        size,
        mass: size,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * ROTATIONV,
        squashX: 1,
        squashY: 1,
      };
    };

    const createHeartElement = (heart: Heart): HTMLDivElement => {
      const div = document.createElement("div");

      div.className = "absolute pointer-events-none";
      div.textContent = "❤️";

      div.style.fontSize = `${heart.size}px`;
      div.style.opacity = "0.15";

      container.appendChild(div);

      return div;
    };

    /* ----------------------------
       Init
    -----------------------------*/

    heartsRef.current = Array.from({ length: count }, createHeart);

    heartElementsRef.current =
      heartsRef.current.map(createHeartElement);

    /* ----------------------------
       Animation
    -----------------------------*/

    const animate = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      const hearts = heartsRef.current;
      const elements = heartElementsRef.current;

      /* -------- Move -------- */

      for (const h of hearts) {
        h.x += h.vx;
        h.y += h.vy;

        h.rotation += h.rotationSpeed;

        h.squashX += (1 - h.squashX) * SQUASH_RECOVERY;
        h.squashY += (1 - h.squashY) * SQUASH_RECOVERY;
      }

      /* -------- Collisions -------- */

      for (let i = 0; i < hearts.length; i++) {
        const a = hearts[i];

        for (let j = i + 1; j < hearts.length; j++) {
          const b: Heart = hearts[j];

          const dx = b.x - a.x;
          const dy = b.y - a.y;

          const distSq = dx * dx + dy * dy;

          const minDist = (a.size + b.size) * 0.5;

          if (distSq >= minDist * minDist) continue;

          const dist = Math.sqrt(distSq) || 0.001;

          const nx = dx / dist;
          const ny = dy / dist;

          /* --- Positional correction --- */

          const overlap = minDist - dist;

          const totalMass = a.mass + b.mass;

          const corrA =
            (b.mass / totalMass) *
            overlap *
            POSITION_CORRECTION;

          const corrB =
            (a.mass / totalMass) *
            overlap *
            POSITION_CORRECTION;

          a.x -= nx * corrA;
          a.y -= ny * corrA;

          b.x += nx * corrB;
          b.y += ny * corrB;

          /* --- Relative velocity --- */

          const rvx = b.vx - a.vx;
          const rvy = b.vy - a.vy;

          const velAlongNormal = rvx * nx + rvy * ny;

          if (velAlongNormal > 0) continue;

          /* --- Impulse --- */

          const invMassA = 1 / a.mass;
          const invMassB = 1 / b.mass;

          const k =
            -(1 + RESTITUTION) *
            velAlongNormal /
            (invMassA + invMassB);

          const ix = k * nx;
          const iy = k * ny;

          a.vx -= ix * invMassA;
          a.vy -= iy * invMassA;

          b.vx += ix * invMassB;
          b.vy += iy * invMassB;

          /* --- Jelly squash --- */

          const strength = Math.min(Math.abs(j) / 20, 1);

          const squash = 1 - strength * 0.25;
          const stretch = 1 + strength * 0.25;

          a.squashX = squash;
          a.squashY = stretch;

          b.squashX = squash;
          b.squashY = stretch;

          /* --- Spin --- */

          const spin =
            (Math.random() - 0.5) * strength * 4;

          a.rotationSpeed += spin;
          b.rotationSpeed -= spin;
        }
      }

      /* -------- Recycle -------- */

      for (const h of hearts) {
        if (
          h.x < -MARGIN ||
          h.x > width + MARGIN ||
          h.y < -MARGIN ||
          h.y > height + MARGIN
        ) {
          const fresh = createHeart();
          Object.assign(h, fresh);
        }
      }

      /* -------- Render -------- */

      for (let i = 0; i < hearts.length; i++) {
        const h = hearts[i];
        const el = elements[i];

        if (!el) continue;

        el.style.transform = `
          translate(${h.x}px, ${h.y}px)
          translate(-50%, -50%)
          rotate(${h.rotation}deg)
          scale(${h.squashX}, ${h.squashY})
        `;
      }

      animationRef.current =
        requestAnimationFrame(animate);
    };

    animate();

    /* ----------------------------
       Cleanup
    -----------------------------*/

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      heartElementsRef.current.forEach((el) =>
        el.remove()
      );
    };
  }, [count]);

  return (
    <div
      ref={canvasRef}
      className="fixed inset-0 overflow-hidden pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
});

BackgroundHearts.displayName = "BackgroundHearts";

export default BackgroundHearts;